import { resolveMentions } from "@/client";

/**
 * Avatars for account mentions that arrived without one: chips pasted in, and
 * chips parsed out of a stored prompt. Both transports carry a mention's
 * identity and nothing else — the id, handle and platform — because the text may
 * be days old and forwarded by someone else, so display fields are resolved from
 * the id instead of taken from it (see the backend's AccountMentionResolver).
 *
 * Resolutions are cached for the life of the page, misses included: the same
 * prompt is re-parsed on every mount and on every `question` prop change, and an
 * id that resolved to nothing is not going to start resolving. A request that
 * *failed* caches nothing, since it says nothing about the ids.
 */

/** Ids per request, matching the cap the endpoint applies. */
const MAX_LOOKUPS = 24;

/** Public id -> avatar URL, or null for "resolves to no avatar". */
const avatarUrlById = new Map<string, string | null>();

/**
 * The avatars for `ids`, as far as they are known. Ids that resolve to nothing —
 * an account the caller can't see, a deleted one, an id that was never ours —
 * are simply absent, so a caller fills in what it can and leaves the rest
 * showing their placeholder glyph.
 */
export async function resolveAccountMentionAvatars(
	ids: string[],
): Promise<Map<string, string>> {
	const unique = [...new Set(ids)];
	const pending = unique
		.filter((id) => !avatarUrlById.has(id))
		.slice(0, MAX_LOOKUPS);

	if (pending.length > 0) {
		try {
			const { data } = await resolveMentions({
				query: { ids: pending.join(",") },
			});
			for (const result of data?.results ?? []) {
				if (result.id) {
					avatarUrlById.set(result.id, result.profile_picture_url ?? null);
				}
			}
			// Whatever the resolver didn't answer for doesn't resolve at all.
			for (const id of pending) {
				if (!avatarUrlById.has(id)) avatarUrlById.set(id, null);
			}
		} catch {
			/* the chips keep their glyph, and the ids stay askable */
		}
	}

	const resolved = new Map<string, string>();
	for (const id of unique) {
		const avatarUrl = avatarUrlById.get(id);
		if (avatarUrl) resolved.set(id, avatarUrl);
	}
	return resolved;
}
